/* AuditPolicyResultsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;

const PagingAuditResultsFeed = require("./PagingAuditResultsFeed");
const Renderer = require("./AuditPolicyResultsViewRenderer");
const moment = require("moment-timezone");
const ConfigColumn = require("./column_renderers/config_line/ConfigLineComponent");
const ViolationsColumn = require("./column_renderers/violations/ViolationsComponent");
const SeverityColumn = require("./column_renderers/severity/SeverityComponent");
const BlocknameColumn = require("./column_renderers/blockname/BlocknameComponent");
const SourceColumn = require("./column_renderers/source/SourceComponent");
const RuleIndexColumn = require("./column_renderers/rule_index/RuleIndexComponent");

const DEFAULT_SORT = {
    deviceName: 1,
    ruleIndex: 1,
    blockName: 1,
};

/**
 * @param {AuditPolicyEditor} gameEditor
 * @param {UserFeedbackComponent} modalUserFeedback
 */
class AuditPolicyResultsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        viewState,
        userSession,
        storageFactory,
        modalUserFeedback,
        io,
        ResultEntity
    ) {
        super();
        this._fileFactory = io.fileFactory;
        this._logger = logger;
        this._resultEntityDef = ResultEntity;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = modalUserFeedback;

        this._pagingFeed = undefined;
        this._initialExecutionEntity = _.get(viewState, "configExecutionEntity");
        this._isSubView = _.get(viewState, "isSubView");
        this._isFrom = _.get(viewState, "isFrom");

        this._gridKey = "configAudit_auditResultsV2";

        this._storageFactory = storageFactory;

        this.columnConfig = {
            grouping: {
                label: "Group",
                width: 220,
                priority: 0,
                shown: false,
                entityColumn: true,
                isGroupingColumn: true,
            },
            deviceName: {
                label: "Device",
                width: 250,
                priority: 0,
                groupable: true,
                shown: true,
            },
            commandSetNames: {
                label: "Command Set",
                width: 200,
                priority: 1,
                shown: true,
                groupable: true,
            },
            commandSetResult: {
                label: "Command Result",
                width: 200,
                priority: 1,
                groupable: true,
                shown: true,
            },
            source: {
                label: "Source",
                width: 90,
                priority: 1,
                columnRendererComponent: SourceColumn,
                groupable: true,
                shown: true,
            },
            snapshotName: {
                label: "Snapshot",
                width: 200,
                priority: 2,
                groupable: true,
                shown: true,
            },
            deviceViolationCount: {
                label: "Device Violation Count",
                shown: false,
                priority: 3,
                width: 120,
                dataType: "number",
            },
            blockName: {
                label: "Block Name",
                width: 200,
                columnRendererComponent: BlocknameColumn,
                priority: 4,
                shown: true,
            },
            severity: {
                label: "Severity",
                columnRendererComponent: SeverityColumn,
                priority: 5,
                width: 120,
                groupable: true,
                options: this._getDataForAdvancedFilter({
                    CRITICAL: "Critical",
                    MAJOR: "Major",
                    MINOR: "Minor",
                    INFO: "Informational",
                }),
            },
            ruleIndex: {
                label: "Rule Order",
                priority: 6,
                columnRendererComponent: RuleIndexColumn,
                dataType: "number",
                width: 100,
                shown: true,
            },
            ruleName: {
                label: "Rule Name",
                sortable: true,
                priority: 7,
                groupable: true,
                width: 200,
                shown: true,
            },
            violations: {
                label: "Violations",
                width: 100,
                sortable: false,
                priority: 8,
                entityColumn: true,
                columnRendererComponent: ViolationsColumn,
                shown: true,
                context: {
                    userFeedback: this._modalUserFeedback,
                },
            },
            configLinesColumn: {
                label: "Config Lines",
                width: 100,
                sortable: false,
                priority: 9,
                entityColumn: true, // Entity passed to renderer, but configLines fetched on-demand
                columnRendererComponent: ConfigColumn,
                shown: true,
                context: {
                    resultEntityDef: this._resultEntityDef,
                    userSession: this._userSession,
                    userFeedback: this._modalUserFeedback,
                },
            },
        };

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _patchSorting(sorting) {
        let defObj = Object.assign({}, DEFAULT_SORT);
        const newKeys = Object.keys(sorting);
        const defKeys = Object.keys(DEFAULT_SORT);
        let sortObj = {};
        newKeys.forEach((key) => {
            sortObj[key] = sorting[key];
            if (defKeys.indexOf(key) >= 0) {
                delete defObj[key];
            }
        });
        sortObj = Object.assign({}, sortObj, defObj);
        return sortObj;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        // document.addEventListener("ViewAuditResults", this._onViewAuditResults = this._onViewAuditResults.bind(this));
        this._gameEditor._LMRBLayout.on(
            "ViewAuditResults",
            (this._onViewAuditResults = this._onViewAuditResults.bind(this))
        );
    }

    _unregisterListeners() {
        // document.removeEventListener("ViewAuditResults", this._onViewAuditResults);
        this._gameEditor._LMRBLayout.removeAllListeners("ViewAuditResults");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get executionEntityData() {
        return this.getExecutionEntityData();
    }

    getExecutionEntityData() {
        return this._executionEntity && this._executionEntity.getPristineProperties();
    }

    get gridKey() {
        return this.getGridKey();
    }

    getGridKey() {
        return this._gridKey;
    }

    get gameEditorRootElement() {
        return this._gameEditor.renderer._rootElem;
    }

    shutdown() {
        this._gameEditor.clearViewActions();
        this._unregisterListeners();

        return Promise.resolve()
            .then(() => this._renderer.shutdown())
            .then(() => {
                if (
                    this._executionEntity &&
                    this._executionEntity !== this._initialExecutionEntity
                ) {
                    let promArr = [];
                    if (this._pagingFeed) {
                        promArr.push(this._pagingFeed.shutdown());
                    }
                    promArr.push(this._executionEntity.shutdown());
                    return Promise.all(promArr);
                } else {
                    Promise.resolve();
                }
            })
            .then(
                () => {
                    this._logger = null;
                    this._gameEditor = null;
                    this._userSession = null;
                    this._modalUserFeedback = null;

                    this._pagingFeed = null;
                    this._executionEntity = null;
                    this._gridKey = null;
                    this.columnConfig = null;
                    this._renderer = null;
                },
                (err) => {
                    this._logger.warn({error: err}, "AuditResultsViewComponent.shutdown() error.");
                }
            );
    }

    whenModelIsReady() {
        return Promise.resolve()
            .then(() => {
                this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                    "entity_grid_gl",
                    this._gridKey
                );

                // Fix for intermittent column visibility bug:
                // Merge saved column order with current columnConfig and update preferences
                const savedColumnOrder = this._userGridData.getColOrder() || [];
                const allColumnIds = Object.keys(this.columnConfig).filter(
                    (colId) => this.columnConfig[colId].shown !== false
                );

                const missingColumns = allColumnIds.filter(
                    (colId) => !savedColumnOrder.includes(colId)
                );

                if (missingColumns.length > 0) {
                    const missingWithPriority = missingColumns.map((colId) => ({
                        id: colId,
                        priority: this.columnConfig[colId].priority || 999,
                    }));
                    missingWithPriority.sort((a, b) => a.priority - b.priority);

                    const mergedColumnOrder = [
                        ...savedColumnOrder.filter((colId) => allColumnIds.includes(colId)),
                        ...missingWithPriority.map((col) => col.id),
                    ];

                    // Update saved preferences so grid's whenModelIsReady() gets the complete list
                    this._userGridData.setData(mergedColumnOrder, "columnOrder");
                }

                return this._userGridData;
            })
            .then(() => this._gameEditor.whenPolicyIsReady())
            .then(() => this._gameEditor.whenConceptItemsAreReady())
            .then(() => this._getExecutionEntity())
            .then((executionEntity) => {
                this._executionEntity = executionEntity;
                if (!executionEntity) {
                    return Promise.reject("no Execution");
                }
                const userSorting = this._userGridData.getSorting() || {};
                const patchedSort = this._patchSorting(userSorting);

                this._pagingFeed = this._createFeed(executionEntity, patchedSort);

                this._registerListeners();

                return Promise.all([
                    Promise.resolve(executionEntity),
                    this._pagingFeed.whenFeedReady(),
                ]);
            })
            .catch((error) => this._logger.warn({error}, "Audit Policy Results View model error."));
    }

    _onViewAuditResults() {
        this.emit("updateResultView");
    }

    onUserSorted(sortData) {
        sortData = [].concat(sortData);
        let sort = {};

        sortData.forEach((sortData) => {
            if (!sortData || !sortData.sortColumn || !this.columnConfig[sortData.sortColumn]) {
                return;
            }
            const sortCriteria =
                this.columnConfig[sortData.sortColumn].valuePath ||
                this.columnConfig[sortData.sortColumn].path ||
                sortData.sortColumn;

            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });

        this._pagingFeed.setSort(this._patchSorting(sort));
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/
    /**
     * Gets Execution Entity, if it is not provided
     * @returns {Promise<ConfigAuditExecutionEntity | Error>}
     * @private
     */
    _getExecutionEntity() {
        const policy = this._gameEditor.getAuditPolicy();

        return this._initialExecutionEntity
            ? Promise.resolve(this._initialExecutionEntity)
            : policy.retrieveMostRecentExecution();
    }

    /**
     * Gets related work
     * @returns {Promise<WorkEntity | Error>}
     * @private
     */
    _getRelatedWork(fields) {
        fields = (fields || ["workInput", "summary", "targets", "targetResults"]).reduce(
            (fieldsObj, field) => _.set(fieldsObj, field, true),
            {}
        );

        return this._getExecutionEntity().then((executionEntity) =>
            executionEntity.loadRelatedWork(fields)
        );
    }

    /**
     * Gets results feed
     * @returns {Promise<EntityChangeFeed | Error>}
     * @private
     */
    _getRelatedFeed() {
        const fields = [
            "deviceName",
            "source",
            "snapshotName",
            "deviceViolationCount",
            "blockName",
            "ruleName",
            "ruleViolationCount",
            "violationType",
            "violation",
            "configLine",
        ].reduce((fieldsObj, field) => _.set(fieldsObj, field, true), {});

        return this._getExecutionEntity().then((executionEntity) =>
            executionEntity.startRelatedResultsFeed({}, fields, {}, {skip: 0, limit: 50})
        );
    }

    /**
     *
     * @param entity
     * @returns {PagingDeviceFeed}
     * @private
     */
    _createFeed(entity, usersort) {
        return new PagingAuditResultsFeed({
            feedKey: this._gridKey + "_feed",
            userSession: this._userSession,
            entityDef: this._resultEntityDef,
            columnConfig: this.columnConfig,
            additionalDataFields: ["evalResults", "ruleIndex"],
            sort: usersort || DEFAULT_SORT,
            relatedEntityDef: entity,
        });
    }

    _getDataForAdvancedFilter(data) {
        let clonedData = _.clone(data);
        let newObj = {};
        for (let key in clonedData) {
            let value = {};
            value[key] = {
                name: key,
                title: clonedData[key],
            };
            newObj = Object.assign(newObj, value);
        }
        return newObj;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    toggleViewAction(options) {
        if (options) {
            this._gameEditor.addViewAction(options);
        } else {
            this._gameEditor.clearViewActions();
        }
    }

    toggleLoadingVeil(state) {
        return state
            ? this._modalUserFeedback.displayLoading()
            : this._modalUserFeedback.hideDisplayed();
    }

    downloadWorkResults(type) {
        const auditPolicyFromExecution = _.get(this._executionEntity, "_pristineProperties"),
            nameFromExecution = _.get(auditPolicyFromExecution, "auditPolicy.name", ""),
            _name = this._gameEditor.getAuditPolicy()
                ? _.get(this._gameEditor.getAuditPolicy(), "name", "")
                : nameFromExecution,
            _execDate = new Date();
        const timeZone = moment.tz.guess(),
            _fileNameTime = moment
                .tz(_execDate.toISOString(), timeZone)
                .format("YYYYMMDD_HHmmss_z");
        let _fileName = `AuditExecution_${_name}_${_fileNameTime}`;
        if (this._gameEditor.currentSolutionName === "device_manager") {
            _fileName = "DM_" + _fileName;
        } else {
            _fileName = "CDA_" + _fileName;
        }
        return this._getExecutionEntity()
            .then((executionEntity) => executionEntity.generateWorkReports())
            .then((data) => {
                let reorganizedData = Object.values(data).reduce((acc, val) => {
                    acc[val.reportType] = val;
                    return acc;
                }, {});

                if (type === "results") {
                    this._fileFactory.saveStringAsFile(
                        reorganizedData.csv.contents,
                        _fileName + ".csv",
                        "text/csv"
                    );
                } else {
                    this._fileFactory.saveJsonAsFile(
                        reorganizedData.json.contents,
                        _fileName + ".json"
                    );
                }

                return Promise.resolve();
            })
            .catch((error) => {
                this._logger.warn({error}, "Audit onDownloadWorkResults - error");
                this._modalUserFeedback.hideDisplayed().then(() => {
                    this._modalUserFeedback.displayError(error);
                });
            });
    }

    /**
     * run async function or functions with the loading veil
     * @param {Promise|Promise[]} promises
     * @returns {Promise<any[] | Error>}
     */
    withLoadingVeil(...promises) {
        return (
            Promise.all([this.toggleLoadingVeil(true), ...promises])
                .catch((error) => {
                    return this.toggleLoadingVeil(false).then(() => {
                        throw new Error(error);
                    });
                })
                // eslint-disable-next-line no-unused-vars
                .then(([veil, ...results]) => {
                    return this.toggleLoadingVeil(false).then(() =>
                        results.length === 1 ? results[0] : results
                    );
                })
        );
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = AuditPolicyResultsViewComponent;

AuditPolicyResultsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.viewState",
    "$teve.userSession",
    "$teve.storageFactory",
    "$teve.ui.userFeedback",
    "$teve.io",
    "$teve.entities.ConfigAuditResult",
];

AuditPolicyResultsViewComponent.$displayDetails = {
    type: "View",
    name: "Audit Execution Results",
    icon: "common.results_link",
    shown: true,
    title: "Audit Policy Results",
    order: 5,
};
